import { Plugin } from 'prosemirror-state'
import { DecorationSet, Decoration, EditorView } from 'prosemirror-view'
import cx from 'classnames'
import { Insertion } from 'common/Prose/nodeViews/TrackedChanges/Insertion'
import { Deletion } from 'common/Prose/nodeViews/TrackedChanges/Deletion'
import { FormattingChange } from 'common/Prose/nodeViews/TrackedChanges/FormattingChange'
import { createNodeDecorations } from './createDecorations'
import { extractTrackedChanges, isTrackedNode, targetElement } from './utils'
import { InitConfig, State } from './Types'
import { TrackChangesView } from './TrackChangesView'
import { getTrackChangesState, trackChangesKey } from './constants'

import MarkViewsStyles from 'common/Prose/nodeViews/TrackedChanges/TrackedChanges.module.scss'
import DecoratorsStyles from './TrackChanges.module.scss'

/*
 * The trackChanges plugin allows to mark all the changes done by users so that
 * they can be viewed and accepted or rejected by reviewers.
 */
export function trackChanges(config: InitConfig) {
  return new Plugin({
    key: trackChangesKey,
    state: {
      init(_, state) {
        const { store, queryClient, dumb = false } = config
        if (!dumb) {
          store.setChanges(extractTrackedChanges(state.doc))
        }
        return {
          store,
          dumb,
          queryClient,
        }
      },
      apply(tr, currentPluginState) {
        const { store, dumb } = currentPluginState
        if (tr.docChanged && !dumb) {
          store.setDocChanged(true)

          store.setChanges(extractTrackedChanges(tr.doc))
        }

        return currentPluginState
      },
    },
    view(view) {
      return new TrackChangesView(view)
    },
    props: {
      nodeViews: {
        insertion: (node, _view, _getPos, _decorations) => new Insertion(node),
        deletion: (node, _view, _getPos, _decorations) => new Deletion(node),
        formatting_change: (node, _view, _getPos, _decorations) => new FormattingChange(node),
      },
      decorations: (editorState) => {
        const { store }: State = getTrackChangesState(editorState)
        const selectedUuid = store.selectedChange?.uuid || undefined
        const { doc } = editorState
        const schema = doc.type.schema
        let decorations: Decoration[] = []

        doc.descendants((node, pos) => {
          if (node.attrs.trackedChanges) {
            createNodeDecorations(node, pos, doc, decorations, selectedUuid)
          }

          if (isTrackedNode(node)) {
            if (selectedUuid) {
              const selectedChangeMark = node.marks.find((mark) => mark.attrs.uuid === selectedUuid)
              if (selectedChangeMark) {
                decorations.push(
                  Decoration.inline(pos, pos + node.nodeSize, {
                    class: cx({
                      [MarkViewsStyles.selected]: [
                        schema.marks.insertion,
                        schema.marks.deletion,
                        schema.marks.formatting_change,
                      ].includes(selectedChangeMark.type),
                    }),
                  }),
                )
              }
            }
          }
        })

        return DecorationSet.create(doc, decorations)
      },
      attributes: (editorState) => {
        const { store }: State = getTrackChangesState(editorState)
        if (store.hideDecorations) {
          return {
            class: cx(MarkViewsStyles.hideDecorations, DecoratorsStyles.hideDecorations),
          }
        } else {
          return {
            class: cx(MarkViewsStyles.showDecorations, DecoratorsStyles.showDecorations),
          }
        }
      },
      handleDOMEvents: {
        click: (view: EditorView, event: Event) => {
          const { store }: State = getTrackChangesState(view.state)

          if (store.hideDecorations) {
            return false
          }

          const element = targetElement(event)
          if (!element) {
            return false
          }
          store.select(element.dataset.uuid)
          store.docStore?.rightRailStore.openSection('trackedChanges')
          return true
        },
        mouseover: (view: EditorView, event: Event) => {
          const { store }: State = getTrackChangesState(view.state)

          if (store.hideDecorations) {
            return false
          }
          const element = targetElement(event)
          if (!element) {
            return false
          }
          store.highlight(element.dataset.uuid)
          return true
        },
        mouseout: (view: EditorView, event: Event) => {
          const { store }: State = getTrackChangesState(view.state)

          if (store.hideDecorations) {
            return false
          }

          const element = targetElement(event)
          if (!element || element.dataset.uuid === store.selectedChange?.uuid) {
            return false
          }
          store.highlight(undefined)
          return true
        },
      },
    },
  })
}
